import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-10 mb-3">{title}</h2>
    <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

export const LegalPage = ({ pageKey }) => {
  const META = {
    privacy: {
      title: 'Privacy Policy | Healthwrx Multispeciality Clinic Guwahati',
      desc: "How Healthwrx collects, uses, stores and protects your personal and health information when you visit our website or book an appointment.",
      slug: 'privacy-policy',
      h1: 'Privacy Policy',
    },
    terms: {
      title: 'Terms of Service | Healthwrx Multispeciality Clinic Guwahati',
      desc: 'The terms that govern your use of the Healthwrx website and our appointment-booking service. Please read carefully before booking.',
      slug: 'terms-of-service',
      h1: 'Terms of Service',
    },
    editorial: {
      title: 'Editorial Policy | Healthwrx Multispeciality Clinic Guwahati',
      desc: 'Our editorial standards: how Healthwrx symptom guides are researched, written, medically reviewed and kept up to date.',
      slug: 'editorial-policy',
      h1: 'Editorial Policy',
    },
  }[pageKey];

  useSeo({
    title: META.title,
    description: META.desc,
    canonical: `https://healthwrx.in/${META.slug}`,
  });

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid={`${META.slug}-page`}>
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 max-w-4xl text-white">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs sm:text-sm text-white/85 mb-4">
            <Link to="/" className="hover:text-white transition">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/50" aria-hidden="true" />
            <span className="text-white font-medium" aria-current="page">{META.h1}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold">{META.h1}</h1>
          <p className="text-sm md:text-base text-white/90 mt-3">Last updated: 27 May 2026</p>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-4xl py-12">
        {pageKey === 'privacy' && <PrivacyContent />}
        {pageKey === 'terms' && <TermsContent />}
        {pageKey === 'editorial' && <EditorialContent />}
      </div>
    </div>
  );
};

const PrivacyContent = () => (
  <>
    <p className="text-gray-700 leading-relaxed">
      Healthwrx Multispeciality Clinic ("we", "us", "our") respects your privacy and is committed to protecting the personal and health information you share with us through our website
      <code className="mx-1 px-1 bg-gray-100 rounded text-sm">healthwrx.in</code>
      and our in-clinic services.
    </p>

    <Section id="information-we-collect" title="1. Information We Collect">
      <p>When you contact us, book an appointment, or visit our website, we may collect:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Identification details:</strong> name, age, gender, phone number, email address.</li>
        <li><strong>Health-related details</strong> that you share with us as part of an appointment request, such as the concern you want to consult about and the preferred department or doctor.</li>
        <li><strong>Appointment metadata:</strong> preferred date and time, doctor selected, and communication history.</li>
        <li><strong>Technical data:</strong> anonymised analytics such as the pages you view, your device type and approximate location, collected through privacy-respecting analytics tools.</li>
      </ul>
    </Section>

    <Section id="how-we-use" title="2. How We Use Your Information">
      <ul className="list-disc pl-6 space-y-1">
        <li>To respond to your appointment request and confirm your visit by phone or WhatsApp.</li>
        <li>To provide medical care during your in-clinic consultation.</li>
        <li>To maintain a record of your visit, in line with applicable medical-record-keeping standards.</li>
        <li>To improve our website and patient-facing content.</li>
      </ul>
      <p>We do <strong>not</strong> sell, rent or share your personal information with third parties for marketing purposes.</p>
    </Section>

    <Section id="how-we-store" title="3. How We Store & Protect Your Data">
      <p>Your data is stored on secure servers and accessed only by authorised Healthwrx staff. We use HTTPS encryption for our website and follow reasonable physical, administrative and technical safeguards to protect your information.</p>
    </Section>

    <Section id="cookies-analytics" title="4. Cookies & Analytics">
      <p>We use a small number of essential cookies to keep the website working correctly, and a privacy-respecting analytics tool to understand how visitors use the site in aggregate. We do not use third-party advertising cookies.</p>
    </Section>

    <Section id="your-rights" title="5. Your Rights">
      <p>You may request to access, correct or delete the personal information we hold about you. To make such a request, please contact us using the details below.</p>
    </Section>

    <Section id="children" title="6. Children's Information">
      <p>For patients under the age of 18, appointment requests should be made by a parent or guardian. We will treat any information about a minor with extra care, as required by applicable law.</p>
    </Section>

    <Section id="changes" title="7. Changes to This Policy">
      <p>We may update this Privacy Policy from time to time. Any changes will be reflected on this page, with the "Last updated" date at the top revised accordingly.</p>
    </Section>

    <Section id="contact" title="8. Contact Us">
      <p>For any questions about this Privacy Policy or your information, please contact:</p>
      <p>
        Healthwrx Multispeciality Clinic<br />
        5th Floor, S.B. Plaza, Rajendra Prasad Road, Last Gate, Guwahati, Assam 781006<br />
        Phone: +91 99926 04456<br />
        Email: <a className="text-[#14D1A2] hover:underline" href="mailto:care@healthwrx.in">care@healthwrx.in</a>
      </p>
    </Section>
  </>
);

const TermsContent = () => (
  <>
    <p className="text-gray-700 leading-relaxed">
      These Terms of Service govern your use of the Healthwrx website at
      <code className="mx-1 px-1 bg-gray-100 rounded text-sm">healthwrx.in</code>
      and any services we offer through it, including appointment booking. By using the website you accept these terms in full.
    </p>

    <Section id="medical-disclaimer" title="1. Medical Disclaimer">
      <p>The content on this website — including symptom guides, doctor profiles and service descriptions — is provided for <strong>general information only</strong>. It is not medical advice and is <strong>not a substitute for consultation with a qualified healthcare provider</strong>. Never disregard professional medical advice, or delay seeking it, because of something you have read on this website.</p>
      <p><strong>In a medical emergency, please call local emergency services or visit the nearest emergency department immediately.</strong></p>
    </Section>

    <Section id="appointments" title="2. Appointment Bookings">
      <p>When you submit an appointment request through our website, you are requesting an in-clinic consultation. Your request is reviewed by our reception team, who will contact you (typically via WhatsApp or phone) to confirm a date and time. Submitting a request does not, by itself, confirm an appointment.</p>
      <p>Please provide accurate and complete information when booking. Healthwrx is not responsible for missed or delayed appointments resulting from incorrect contact details.</p>
    </Section>

    <Section id="use-of-website" title="3. Acceptable Use of the Website">
      <p>You agree to use this website only for lawful purposes. You agree not to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Use the website in any way that could harm, disable or impair its functionality.</li>
        <li>Attempt to access any administrative or restricted areas without authorisation.</li>
        <li>Use automated tools to scrape large volumes of content without permission.</li>
        <li>Submit false, misleading or fraudulent appointment requests.</li>
      </ul>
    </Section>

    <Section id="intellectual-property" title="4. Intellectual Property">
      <p>All content on this website — text, images, logos and design — is owned by or licensed to Healthwrx. You may view and share content for personal, non-commercial use, including linking to our pages. You may not republish or commercially redistribute our content without prior written permission.</p>
    </Section>

    <Section id="external-links" title="5. External Links">
      <p>Our website may contain links to external sites. We are not responsible for the content, accuracy or privacy practices of any external site you choose to visit.</p>
    </Section>

    <Section id="liability" title="6. Limitation of Liability">
      <p>To the maximum extent permitted by applicable law, Healthwrx is not liable for any indirect or consequential loss arising from your use of, or inability to use, this website or its content. Nothing in these terms limits liability that cannot be excluded by law.</p>
    </Section>

    <Section id="changes-terms" title="7. Changes to These Terms">
      <p>We may update these Terms of Service from time to time. The current version is always shown on this page, with the "Last updated" date at the top revised accordingly. Continued use of the website constitutes acceptance of any changes.</p>
    </Section>

    <Section id="governing-law" title="8. Governing Law">
      <p>These Terms of Service are governed by the laws of India. Any disputes will be subject to the exclusive jurisdiction of the courts in Guwahati, Assam.</p>
    </Section>

    <Section id="contact-terms" title="9. Contact">
      <p>For any questions about these Terms, please contact Healthwrx at +91 99926 04456 or <a className="text-[#14D1A2] hover:underline" href="mailto:care@healthwrx.in">care@healthwrx.in</a>.</p>
    </Section>
  </>
);

const EditorialContent = () => (
  <>
    <p className="text-gray-700 leading-relaxed">
      Healthwrx publishes patient-facing symptom guides to help people in and around Guwahati understand common health concerns and decide when to see a specialist. This Editorial Policy explains how that content is created and kept current.
    </p>

    <Section id="purpose" title="1. Purpose of Our Content">
      <p>Our guides are intended to be a <strong>plain-language, trustworthy first stop</strong> for someone wondering about a symptom — not a replacement for a doctor. Every guide is designed to help readers recognise warning signs and seek appropriate professional care.</p>
    </Section>

    <Section id="how-written" title="2. How Our Guides Are Written">
      <ul className="list-disc pl-6 space-y-1">
        <li>Each guide is drafted in line with patient-education best practice — clear language, structured sections, and links to the relevant Healthwrx specialist.</li>
        <li>Content draws on established medical references and clinical experience of our specialists.</li>
        <li>We avoid sensationalism, exaggerated claims and any content that could discourage someone from seeking professional care.</li>
      </ul>
    </Section>

    <Section id="medical-review" title="3. Medical Review">
      <p>Every symptom guide is <strong>reviewed by the relevant Healthwrx specialist</strong> before publication — for example, urology guides are reviewed by our urologist, dermatology guides by our dermatologist, and so on. The reviewing specialist and the last reviewed date are shown on each guide.</p>
    </Section>

    <Section id="updates" title="4. Updates & Corrections">
      <p>We review and update our guides periodically, and whenever practice changes meaningfully. If you notice an error, please email <a className="text-[#14D1A2] hover:underline" href="mailto:care@healthwrx.in">care@healthwrx.in</a> with the page link and the concern. We aim to respond to corrections quickly.</p>
    </Section>

    <Section id="independence" title="5. Independence & Conflicts of Interest">
      <p>Our editorial content is not sponsored. We do not accept payment in exchange for clinical recommendations, and we do not run third-party display advertising. Where we link to external resources, those links are chosen for their educational quality.</p>
    </Section>

    <Section id="ymyl" title="6. Your Money or Your Life (YMYL) & Limitations">
      <p>Health content is "Your Money or Your Life" content under search-engine guidelines, and we treat it accordingly. However, web content can never replace an in-person consultation. If your symptoms are severe, worsening or worrying, please contact Healthwrx or visit an emergency facility without delay.</p>
    </Section>

    <Section id="contact-editorial" title="7. Contact the Editorial Team">
      <p>To suggest a topic, report an inaccuracy or ask about our process, please contact us at <a className="text-[#14D1A2] hover:underline" href="mailto:care@healthwrx.in">care@healthwrx.in</a>.</p>
    </Section>
  </>
);

export default LegalPage;
